import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import type { LeadIntel, Lead, SampleRequest } from "../../../types/types";
import { H3 } from "../../../components/Typography/Typography";
import { TertiaryButtonMedium } from "../../../components/Buttons/Buttons";
import {
  CaretDownIcon,
  LocationIcon,
  LinkedinIcon,
  LinkIcon,
  CompanyIconV2,
  CircleCheckIcon,
  ProductsIconV2,
  InfoCircleIcon,
} from "../../../components/Icons/Icons";
import logoAlchemist from "../../../components/AlchemistInsightsBanner/logo_alchemist.png";
import { useRoutePath } from "../../../util/Routing";

// Accordion wrapper matching Figma card style
const AccordionSection = styled.div`
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};

  &:last-child {
    margin-bottom: 0;
  }
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
  padding-bottom: 0;
`;

const ChevronButton = styled.button`
  background: transparent;
  border: none;
  padding: ${({ theme }) => theme.resolve("spacing_xs")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &.expanded {
    transform: rotate(180deg);
  }
`;

const SectionTitle = styled.div`
  flex: 1;
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  font-size: ${({ theme }) => theme.resolve("font_size_xl")};
  line-height: ${({ theme }) => theme.resolve("line_height_xl")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
`;

const AccordionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

// Info block components matching Figma
const InfoRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const InfoBlockVertical = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
  flex: 1;
  min-width: 0;
`;

const InfoLabel = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  color: ${({ theme }) => theme.resolve("color_text_tertiary")};
`;

const InfoValue = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
`;

const IconWrapper = styled.div`
  width: ${({ theme }) => theme.resolve("spacing_2xl")};
  height: ${({ theme }) => theme.resolve("spacing_2xl")};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderWithLogo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const LogoImage = styled.img`
  width: 35.68px;
  height: ${({ theme }) => theme.resolve("spacing_4xl")};
`;

// Markets served tags
const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  margin-top: ${({ theme }) => theme.resolve("spacing_xxs")};
`;

const Tag = styled.div`
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  white-space: nowrap;
`;

const ProductTag = styled.a<{ clickable?: boolean }>`
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  text-decoration: none;
  pointer-events: ${({ clickable }) => (clickable ? "auto" : "none")};
`;

// Button group for Website/LinkedIn
const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  align-items: flex-start;
`;

// Product card (for Company's Products section)
const ProductCard = styled.div`
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
`;

const ProductName = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
`;

const ProductDescription = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  margin-top: ${({ theme }) => theme.resolve("spacing_sm")};
`;

// Sub-accordion for nested sections (Usage Insights, Opportunities)
const SubAccordionWrapper = styled.div`
  background-color: transparent;
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
`;

const SubAccordionHeader = styled.div<{ $hasContent: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  cursor: pointer;
  user-select: none;
  margin-bottom: ${({ $hasContent }) => ($hasContent ? "0" : "0")};
`;

const SubSectionTitle = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  font-size: ${({ theme }) => theme.resolve("font_size_base")};
  line-height: ${({ theme }) => theme.resolve("line_height_base")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
`;

const SubSectionSubtitle = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  color: ${({ theme }) => theme.resolve("color_text_tertiary")};
  margin-top: ${({ theme }) => theme.resolve("spacing_xxs")};
`;

const SubAccordionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const InsightItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  align-items: flex-start;
`;

const InsightText = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  flex: 1;
`;

const OpportunityTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  margin-top: ${({ theme }) => theme.resolve("spacing_xs")};
`;

// Similar Customers
const SimilarCustomersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const CustomerCard = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const CustomerName = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
`;

const CustomerLocation = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
`;

const CustomerDetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  width: 100%;
`;

const CustomerDetailLabel = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const CustomerDetailText = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")},
    sans-serif;
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  font-size: ${({ theme }) => theme.resolve("font_size_md")};
  line-height: ${({ theme }) => theme.resolve("line_height_md")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  flex: 1;
`;

interface LeadIntelDisplayProps {
  leadIntel: LeadIntel;
  lead: Lead | SampleRequest;
}

interface AccordionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  defaultExpanded = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <AccordionSection>
      <AccordionHeader onClick={() => setIsExpanded(!isExpanded)}>
        <ChevronButton className={isExpanded ? "expanded" : ""}>
          <CaretDownIcon width={16} height={16} />
        </ChevronButton>
        <SectionTitle>{title}</SectionTitle>
      </AccordionHeader>
      {isExpanded && <AccordionContent>{children}</AccordionContent>}
    </AccordionSection>
  );
};

interface SubAccordionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const SubAccordion: React.FC<SubAccordionProps> = ({
  title,
  subtitle,
  children,
  defaultExpanded = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <SubAccordionWrapper>
      <SubAccordionHeader
        onClick={() => setIsExpanded(!isExpanded)}
        $hasContent={isExpanded && !!children}
      >
        <ChevronButton className={isExpanded ? "expanded" : ""}>
          <CaretDownIcon width={16} height={16} />
        </ChevronButton>
        <div style={{ flex: 1 }}>
          <SubSectionTitle>{title}</SubSectionTitle>
        </div>
      </SubAccordionHeader>
      {isExpanded && subtitle && (
        <SubSectionSubtitle>{subtitle}</SubSectionSubtitle>
      )}
      {isExpanded && <SubAccordionContent>{children}</SubAccordionContent>}
    </SubAccordionWrapper>
  );
};

export const LeadIntelDisplay: React.FC<LeadIntelDisplayProps> = ({
  leadIntel,
  lead,
}) => {
  const { t } = useTranslation();
  const { adminPath } = useRoutePath();

  // Helper to format location
  const formatLocation = (city?: string, state?: string, country?: string) => {
    const parts = [city, state, country].filter(Boolean);
    return parts.join(", ");
  };

  // Determine if this is a Lead or SampleRequest
  const isSampleRequest = "items" in lead && !("cart" in lead);

  // Get location based on type
  const companyLocation = isSampleRequest
    ? (lead as SampleRequest).buyer_address
      ? formatLocation(
          (lead as SampleRequest).buyer_address?.city,
          (lead as SampleRequest).buyer_address?.state,
          (lead as SampleRequest).buyer_address?.country
        )
      : null
    : "address" in lead && lead.address
    ? formatLocation(
        lead.address.city,
        lead.address.state_display_name || lead.address.state,
        lead.address.country
      )
    : null;

  const buyerFullName =
    [lead.buyer_first_name, lead.buyer_last_name].filter(Boolean).join(" ") ||
    "--";

  return (
    <>
      <HeaderWithLogo>
        <LogoImage src={logoAlchemist} alt="Alchemist AI" />
        <H3 style={{ margin: 0 }}>{t("Alchemist Insights")}</H3>
      </HeaderWithLogo>

      {/* Company Section */}
      <Accordion title={t("Company")}>
        <InfoRow>
          <InfoBlockVertical>
            <InfoLabel>{t("Name")}</InfoLabel>
            <InfoValue>
              <IconWrapper>
                <CompanyIconV2 width={20} height={20} />
              </IconWrapper>
              {lead.buyer_company_name || "--"}
            </InfoValue>
          </InfoBlockVertical>
          {companyLocation && (
            <InfoBlockVertical>
              <InfoLabel>{t("Location")}</InfoLabel>
              <InfoValue>
                <IconWrapper>
                  <LocationIcon width={20} height={20} />
                </IconWrapper>
                {companyLocation}
              </InfoValue>
            </InfoBlockVertical>
          )}
        </InfoRow>

        {leadIntel.summary && (
          <InfoBlockVertical>
            <InfoLabel>{t("Summary")}</InfoLabel>
            <InfoValue>{leadIntel.summary}</InfoValue>
          </InfoBlockVertical>
        )}

        {leadIntel.markets_served && leadIntel.markets_served.length > 0 && (
          <InfoBlockVertical>
            <InfoLabel>{t("Markets Served")}</InfoLabel>
            <TagsContainer>
              {leadIntel.markets_served.map((market, index) => (
                <Tag key={index}>{market}</Tag>
              ))}
            </TagsContainer>
          </InfoBlockVertical>
        )}

        <ButtonGroup>
          {leadIntel.website && (
            <TertiaryButtonMedium
              as="a"
              href={leadIntel.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                height: "32px",
                padding: "6px 12px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                textDecoration: "none",
              }}
            >
              <LinkIcon width={20} height={20} />
              {t("Website")}
            </TertiaryButtonMedium>
          )}
          {leadIntel.linkedin && (
            <TertiaryButtonMedium
              as="a"
              href={leadIntel.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                height: "32px",
                padding: "6px 12px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                textDecoration: "none",
              }}
            >
              <LinkedinIcon width={20} height={20} />
              {t("LinkedIn")}
            </TertiaryButtonMedium>
          )}
        </ButtonGroup>
      </Accordion>

      {/* Contact Details Section */}
      <Accordion title={t("Contact Details")}>
        <InfoRow>
          <InfoBlockVertical>
            <InfoLabel>{t("Name")}</InfoLabel>
            <InfoValue>{buyerFullName}</InfoValue>
          </InfoBlockVertical>
          <InfoBlockVertical>
            <InfoLabel>{t("Title")}</InfoLabel>
            <InfoValue>{leadIntel.contact_title || "--"}</InfoValue>
          </InfoBlockVertical>
        </InfoRow>
        {companyLocation && (
          <InfoRow>
            <InfoBlockVertical>
              <InfoLabel>{t("Location")}</InfoLabel>
              <InfoValue>{companyLocation}</InfoValue>
            </InfoBlockVertical>
          </InfoRow>
        )}

        {leadIntel.contact_linkedin && (
          <ButtonGroup>
            <TertiaryButtonMedium
              as="a"
              href={leadIntel.contact_linkedin}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                height: "32px",
                padding: "6px 12px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                textDecoration: "none",
              }}
            >
              <LinkedinIcon width={20} height={20} />
              {t("LinkedIn")}
            </TertiaryButtonMedium>
          </ButtonGroup>
        )}
      </Accordion>

      {/* Company's Products Section */}
      {leadIntel.lead_company_products &&
        leadIntel.lead_company_products.length > 0 && (
          <Accordion title={t("Company's Products")}>
            {leadIntel.lead_company_products.map((product, index) => (
              <ProductCard key={index}>
                {product.product_name && (
                  <ProductName>{product.product_name}</ProductName>
                )}
                {product.product_description && (
                  <ProductDescription>
                    {product.product_description}
                  </ProductDescription>
                )}
              </ProductCard>
            ))}
          </Accordion>
        )}

      {/* Usage Insights Section */}
      {leadIntel.cart_product_insights &&
        leadIntel.cart_product_insights.length > 0 && (
          <Accordion title={t("Usage Insights")}>
            {leadIntel.cart_product_insights.map((insight, index) => {
              const trimmedProductName = insight.product_name?.trim();

              // Find matching item for this product
              const matchingItem = isSampleRequest
                ? (lead as SampleRequest).items.find(
                    (item) => item.product.name?.trim() === trimmedProductName
                  )
                : "cart" in lead && lead.cart?.items
                ? lead.cart.items.find(
                    (item) => item.product.name?.trim() === trimmedProductName
                  )
                : null;

              return (
                <SubAccordion
                  key={index}
                  title={trimmedProductName || t("Product")}
                >
                  {/* Purpose/Application chips */}
                  {matchingItem &&
                    (() => {
                      const tags: Array<{ key: string; label: string }> = [];

                      if (
                        isSampleRequest &&
                        "purpose" in matchingItem &&
                        matchingItem.purpose
                      ) {
                        tags.push({
                          key: "purpose",
                          label: matchingItem.purpose,
                        });
                      }

                      const applications = isSampleRequest
                        ? "applications" in matchingItem
                          ? matchingItem.applications
                          : []
                        : "product_applications" in matchingItem
                        ? matchingItem.product_applications
                        : [];

                      const customApplication =
                        isSampleRequest && "custom_application" in matchingItem
                          ? matchingItem.custom_application
                          : null;

                      if (applications && applications.length > 0) {
                        applications.forEach((app) => {
                          tags.push({ key: `app-${app.id}`, label: app.name });
                        });
                      }

                      if (customApplication) {
                        tags.push({
                          key: "custom-application",
                          label: customApplication,
                        });
                      }

                      return (
                        tags.length > 0 && (
                          <TagsContainer>
                            {tags.map(({ key, label }) => (
                              <Tag key={key}>{label}</Tag>
                            ))}
                          </TagsContainer>
                        )
                      );
                    })()}

                  {/* Usage insights */}
                  {insight.usage_insights &&
                    insight.usage_insights.map((usageText, idx) => (
                      <InsightItem key={idx}>
                        <IconWrapper>
                          <CircleCheckIcon width={20} height={20} />
                        </IconWrapper>
                        <InsightText>{usageText}</InsightText>
                      </InsightItem>
                    ))}
                </SubAccordion>
              );
            })}
          </Accordion>
        )}

      {/* Upsell Opportunities Section */}
      {leadIntel.opportunities && leadIntel.opportunities.length > 0 && (
        <Accordion title={t("Upsell Opportunities")}>
          {leadIntel.opportunities.map((opp, index) => (
            <SubAccordion
              key={index}
              title={opp.function || t("Opportunity")}
              subtitle={opp.function_description || undefined}
            >
              {opp.products && opp.products.length > 0 && (
                <InfoBlockVertical>
                  <OpportunityTagsContainer>
                    {opp.products.map((product, pIndex) => (
                      <ProductTag
                        key={pIndex}
                        clickable={!!product.product_number}
                        href={
                          product.product_number
                            ? `${adminPath}/pim/products/${product.product_number}`
                            : undefined
                        }
                        // onClick={(e: React.MouseEvent) => {
                        //   if (
                        //     product.product_number &&
                        //     !e.metaKey &&
                        //     !e.ctrlKey
                        //   ) {
                        //     e.preventDefault();
                        //     history.push(
                        //       `${adminPath}/pim/products/${product.product_number}`
                        //     );
                        //   }
                        // }}
                      >
                        <ProductsIconV2 width={16} height={16} />
                        {product.product_name}
                        {product.product_number &&
                          ` (${product.product_number})`}
                      </ProductTag>
                    ))}
                  </OpportunityTagsContainer>
                </InfoBlockVertical>
              )}
            </SubAccordion>
          ))}
        </Accordion>
      )}

      {/* Similar Customers Section */}
      {leadIntel.similar_customers && leadIntel.similar_customers.length > 0 && (
        <Accordion title={t("Similar Customers")}>
          <SimilarCustomersContainer>
            {leadIntel.similar_customers.map((customer, index) => {
              const location = formatLocation(
                customer.city || undefined,
                customer.state || undefined,
                customer.country || undefined
              );

              const websiteUrl = customer.website?.startsWith("http")
                ? customer.website
                : `https://${customer.website}`;

              return (
                <CustomerCard key={index}>
                  {customer.customer_name && (
                    <CustomerName>
                      {index + 1}. {customer.customer_name}
                    </CustomerName>
                  )}
                  {location !== "--" && (
                    <CustomerLocation>{location}</CustomerLocation>
                  )}
                  {customer.overview && (
                    <CustomerDetailSection>
                      <CustomerDetailLabel>
                        <InfoCircleIcon width={16} height={16} />
                        {t("Overview")}
                      </CustomerDetailLabel>
                      <CustomerDetailText>
                        {customer.overview}
                      </CustomerDetailText>
                    </CustomerDetailSection>
                  )}
                  {customer.relevance && (
                    <CustomerDetailSection>
                      <CustomerDetailLabel>
                        <CircleCheckIcon width={16} height={16} />
                        {t("Relevance")}
                      </CustomerDetailLabel>
                      <CustomerDetailText>
                        {customer.relevance}
                      </CustomerDetailText>
                    </CustomerDetailSection>
                  )}
                  {customer.website &&
                    customer.website !== "(website not available)" && (
                      <TertiaryButtonMedium
                        as="a"
                        href={websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          width: "fit-content",
                          height: "24px",
                          padding: "2px 8px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          textDecoration: "none",
                          fontSize: "12px",
                        }}
                      >
                        <LinkIcon width={16} height={16} />
                        {customer.website}
                      </TertiaryButtonMedium>
                    )}
                </CustomerCard>
              );
            })}
          </SimilarCustomersContainer>
        </Accordion>
      )}
    </>
  );
};
